// Import hooks.
import {
	useInputFormCache,
	UseInputFormCacheResult,
} from '@app/hooks/view/input';
// Import services.
import {
	AddressService,
} from '@app/services';
// Import app models.
import {
	ValidatorResponse,
} from '@app/models/app';
// import api models.
import {
	AddressLocationWithAccuracy,
} from '@app/models/api';
// Import props.
import {
	InputBuildingAddressSummaryProps,
	InputFormProps,
} from '@app/props';
// Import enumerations
import {
	GpsAccuracy,
} from '@app/enumerations';

interface UseInputBuildingAddressSummaryParameters {
	id: string;
	label: string;
	required: boolean;
	disabled: boolean;
	value: AddressLocationWithAccuracy;
	showValidation?: boolean;
	onChangeAddress: () => void;
	onChangeLocation: () => void;
}

type UseInputBuildingAddressSummaryResults = UseInputFormCacheResult<AddressLocationWithAccuracy, InputBuildingAddressSummaryProps>;

const useInputBuildingAddressSummary = (params: UseInputBuildingAddressSummaryParameters): UseInputBuildingAddressSummaryResults => {

	// #region exported methods

	/**
	 * Returns props for form input. 
	 **/
	const applyInputBuildingAddressSummaryProps = (): InputBuildingAddressSummaryProps => {
		// First apply the form input props.
		let formProps: InputFormProps = applyProps();
		// Then apply the building address summary input props.
		return {
			...formProps,
			addressLineDisplay: params.value.address !== null ? AddressService.getInstance().formatAddressToSingleLineStringWithTitleCasing(params.value.address) : undefined,
			onChangeAddress: params.onChangeAddress,
			onChangeLocation: areGpsCoordinatesMissing(params.value) ? undefined : params.onChangeLocation,
		}
	};

	// #endregion exported methods


	// #region private methods

	/**
	 * Checks to see if the gps coordinates exist or have a value of 0 for this address.
	 * @param address
	 */
	const areGpsCoordinatesMissing = (address: AddressLocationWithAccuracy | undefined): boolean => {
		const lat: number | undefined = address?.gpsCoordinates?.latitude ?? undefined;
		const lng: number | undefined = address?.gpsCoordinates?.longitude ?? undefined;

		return lat === 0 || lng === 0 || lat === undefined || lng === undefined;
	}

	/**
	 * Checks to see if the gps accuracy is acceptable.
	 * @param address
	 */
	const isGpsAccuracyAcceptable = (address: AddressLocationWithAccuracy | undefined): boolean => {
		return address?.gpsCoordinates?.gpsAccuracy === GpsAccuracy.ROOFTOP
			|| address?.gpsCoordinates?.gpsAccuracy === GpsAccuracy.PRODUCER_SELECTED
			|| address?.gpsCoordinates?.gpsAccuracy === GpsAccuracy.UNDERWRITER_SELECTED;
	}

	/**
	 * Validates the current value.
	 * @param value
	 */
	const validateAddressLocation = (value: AddressLocationWithAccuracy, required: boolean): ValidatorResponse => {
		let response: ValidatorResponse = new ValidatorResponse(true, String.empty());

		if (required && (value.address === null || AddressService.getInstance().isAddressMissingInformation(value.address))) {
			response = new ValidatorResponse(false, 'Address is incomplete.  Please fix any incomplete information.');
		}
		else if (areGpsCoordinatesMissing(value) || !isGpsAccuracyAcceptable(value)) {
			response = new ValidatorResponse(false, 'The address geo-location is missing.  Please fix the issue by saving the address again.');
		}

		return response;
	}

	// #endregion private methods


	// #region variables

	// Instantiate the 'Address Location' input cache.
	const { applyProps, ...other } = useInputFormCache<AddressLocationWithAccuracy>({
		id: params.id,
		label: params.label,
		required: params.required,
		disabled: params.disabled,
		inputToCache: params.value,
		showValidation: params.showValidation,
		validators: [validateAddressLocation],
	});

	// #endregion variables

	return {
		applyProps: applyInputBuildingAddressSummaryProps,
		...other,
	};
}

// Export parameters.
export {
	UseInputBuildingAddressSummaryParameters,
}

// Export results.
export {
	UseInputBuildingAddressSummaryResults,
}

export default useInputBuildingAddressSummary;